import React, { useEffect, useState } from "react";
import { apiClient } from "@/lib/apiClient";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export function PayrollConfigPage() {
  const [config, setConfig] = useState({
    ahv_rate_employee: "5.3",
    ahv_rate_employer: "5.3",
    alv_rate_employee: "1.1",
    alv_rate_employer: "1.1",
    bvg_rate_employee: "7.0",
    bvg_rate_employer: "7.0",
    bvg_mode: "legal",
    bvg_entry_threshold_annual: "22680",
    bvg_coordination_deduction_annual: "26460",
    bvg_min_coordinated_annual: "3780",
    bvg_max_salary_annual: "90720",
    bvg_age_rates: { "25-34": "7", "35-44": "10", "45-54": "15", "55-65": "18" },
    bvg_employer_share: "50",
    bvg_risk_rate_under_25: "0",
    bvg_default_rate_total: "7",
    insurance_rate_employee: "0.0",
    insurance_rate_employer: "0.0",
    fak_canton: "ZH",
    fak_rate_employer: "1.125",
  });
  const [fakRates, setFakRates] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const companyId = localStorage.getItem("demoCompanyId");

  const loadConfig = async () => {
    if (!companyId) return;
    setLoading(true);
    setError("");
    try {
      const res = await apiClient.get(`/companies/${companyId}/payroll-config`);
      const data = res.data;
      const pct = (v) => String(Math.round(((v ?? 0) * 100) * 10000) / 10000);
      setConfig({
        ahv_rate_employee: pct(data.ahv_rate_employee),
        ahv_rate_employer: pct(data.ahv_rate_employer),
        alv_rate_employee: pct(data.alv_rate_employee),
        alv_rate_employer: pct(data.alv_rate_employer),
        bvg_rate_employee: pct(data.bvg_rate_employee),
        bvg_rate_employer: pct(data.bvg_rate_employer),
        bvg_mode: data.bvg_mode || "legal",
        bvg_entry_threshold_annual: String(data.bvg_entry_threshold_annual ?? 22680),
        bvg_coordination_deduction_annual: String(data.bvg_coordination_deduction_annual ?? 26460),
        bvg_min_coordinated_annual: String(data.bvg_min_coordinated_annual ?? 3780),
        bvg_max_salary_annual: String(data.bvg_max_salary_annual ?? 90720),
        bvg_age_rates: Object.fromEntries(Object.entries(data.bvg_age_rates || { "25-34": 0.07, "35-44": 0.10, "45-54": 0.15, "55-65": 0.18 }).map(([k, v]) => [k, pct(v)])),
        bvg_employer_share: pct(data.bvg_employer_share ?? 0.5),
        bvg_risk_rate_under_25: pct(data.bvg_risk_rate_under_25 ?? 0),
        bvg_default_rate_total: pct(data.bvg_default_rate_total ?? 0.07),
        insurance_rate_employee: pct(data.insurance_rate_employee),
        insurance_rate_employer: pct(data.insurance_rate_employer),
        fak_canton: data.fak_canton || "ZH",
        fak_rate_employer: pct(data.fak_rate_employer ?? 0.01125),
      });
      try {
        const fr = await apiClient.get(`/companies/${companyId}/payroll-config/fak-rates`);
        setFakRates(fr.data.rates_percent || {});
      } catch { /* optional */ }
    } catch (e) {
      console.error(e);
      setError(e.response?.data?.detail || "Fehler beim Laden der Lohnkonfiguration.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadConfig();
  }, []);

  const handleChange = (field, value) => {
    setConfig((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!companyId) return;
    setSaving(true);
    setError("");
    try {
      const payload = {
        company_id: companyId,
        ahv_rate_employee: (parseFloat(config.ahv_rate_employee) || 0) / 100,
        ahv_rate_employer: (parseFloat(config.ahv_rate_employer) || 0) / 100,
        alv_rate_employee: (parseFloat(config.alv_rate_employee) || 0) / 100,
        alv_rate_employer: (parseFloat(config.alv_rate_employer) || 0) / 100,
        bvg_rate_employee: (parseFloat(config.bvg_rate_employee) || 0) / 100,
        bvg_rate_employer: (parseFloat(config.bvg_rate_employer) || 0) / 100,
        bvg_mode: config.bvg_mode,
        bvg_entry_threshold_annual: parseFloat(config.bvg_entry_threshold_annual) || 0,
        bvg_coordination_deduction_annual: parseFloat(config.bvg_coordination_deduction_annual) || 0,
        bvg_min_coordinated_annual: parseFloat(config.bvg_min_coordinated_annual) || 0,
        bvg_max_salary_annual: parseFloat(config.bvg_max_salary_annual) || 0,
        bvg_age_rates: Object.fromEntries(Object.entries(config.bvg_age_rates).map(([k, v]) => [k, (parseFloat(v) || 0) / 100])),
        bvg_employer_share: (parseFloat(config.bvg_employer_share) || 0) / 100,
        bvg_risk_rate_under_25: (parseFloat(config.bvg_risk_rate_under_25) || 0) / 100,
        bvg_default_rate_total: (parseFloat(config.bvg_default_rate_total) || 0) / 100,
        insurance_rate_employee: (parseFloat(config.insurance_rate_employee) || 0) / 100,
        insurance_rate_employer: (parseFloat(config.insurance_rate_employer) || 0) / 100,
        fak_canton: config.fak_canton,
        fak_rate_employer: (parseFloat(config.fak_rate_employer) || 0) / 100,
      };
      await apiClient.put(`/companies/${companyId}/payroll-config`, payload);
    } catch (e) {
      console.error(e);
      setError(e.response?.data?.detail || "Fehler beim Speichern der Lohnkonfiguration.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="payroll-config-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight text-slate-900">Lohn-/SV-Konfiguration</h1>
          <p className="mt-1 text-sm text-slate-500">
            Hinterlegen Sie vereinfachte Prozentsätze für AHV/ALV/BVG und Versicherungen (Arbeitnehmer/Arbeitgeber).
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          data-testid="payroll-config-official-rates-btn"
          onClick={async () => {
            if (!window.confirm("Offizielle Schweizer Sätze 2026 übernehmen?\n\nAHV 5.3% · ALV 1.1% · BVG 7% (Richtwert) · NBU 1.6% (Richtwert) · UVGZ 0.25% · KTG 0.6%\n\nBestehende Werte werden überschrieben — danach weiterhin manuell anpassbar.")) return;
            try {
              const companyIdLocal = localStorage.getItem("demoCompanyId");
              const r = await apiClient.post(`/companies/${companyIdLocal}/payroll-config/official-defaults`);
              window.alert(r.data.message);
              window.location.reload();
            } catch (e) {
              window.alert(e.response?.data?.detail || "Fehlgeschlagen");
            }
          }}
        >
          Offizielle Sätze 2026 übernehmen
        </Button>
      </div>

      {error && (
        <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-800">
          {error}
        </div>
      )}

      <Card className="p-4 max-w-3xl border border-slate-200 bg-white shadow-sm" data-testid="payroll-config-card">
        <form className="space-y-4" onSubmit={handleSave} data-testid="payroll-config-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1">
              <Label htmlFor="ahv-emp">AHV AN (%)</Label>
              <Input
                id="ahv-emp"
                type="number"
                value={config.ahv_rate_employee}
                onChange={(e) => handleChange("ahv_rate_employee", e.target.value)}
                data-testid="payroll-ahv-employee-input"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="ahv-er">AHV AG (%)</Label>
              <Input
                id="ahv-er"
                type="number"
                value={config.ahv_rate_employer}
                onChange={(e) => handleChange("ahv_rate_employer", e.target.value)}
                data-testid="payroll-ahv-employer-input"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1">
              <Label htmlFor="alv-emp">ALV AN (%)</Label>
              <Input
                id="alv-emp"
                type="number"
                value={config.alv_rate_employee}
                onChange={(e) => handleChange("alv_rate_employee", e.target.value)}
                data-testid="payroll-alv-employee-input"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="alv-er">ALV AG (%)</Label>
              <Input
                id="alv-er"
                type="number"
                value={config.alv_rate_employer}
                onChange={(e) => handleChange("alv_rate_employer", e.target.value)}
                data-testid="payroll-alv-employer-input"
              />
            </div>
          </div>

          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-3" data-testid="payroll-bvg-section">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <div className="text-sm font-semibold text-slate-800">Pensionskasse (BVG)</div>
                <p className="text-[11px] text-slate-500">Jede Pensionskasse hat ihr eigenes Reglement – das Gesetz setzt nur Minima. Werte gemäss eurem PK-Vertrag anpassen.</p>
              </div>
              <select value={config.bvg_mode} onChange={(e) => handleChange("bvg_mode", e.target.value)}
                className="border border-slate-200 rounded-md px-2 py-1 text-sm" data-testid="payroll-bvg-mode-select">
                <option value="legal">Nach Gesetz: Eintrittsschwelle + Koordinationsabzug + Altersstaffel</option>
                <option value="flat">Pauschal: fester % vom ganzen AHV-Lohn</option>
              </select>
            </div>
            {config.bvg_mode === "legal" ? (
              <>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {[["bvg_entry_threshold_annual", "Eintrittsschwelle (CHF/Jahr)", "Darunter keine BVG-Pflicht (1'890/Monat)"],
                    ["bvg_coordination_deduction_annual", "Koordinationsabzug (CHF/Jahr)", "wird vom Jahreslohn abgezogen"],
                    ["bvg_min_coordinated_annual", "Min. koordinierter Lohn (CHF/Jahr)", ""],
                    ["bvg_max_salary_annual", "Max. versicherter Lohn (CHF/Jahr)", "obligatorischer Teil"]].map(([k, label, hint]) => (
                    <div className="space-y-1" key={k}>
                      <Label htmlFor={k} className="text-xs">{label}</Label>
                      <Input id={k} type="number" value={config[k]} onChange={(e) => handleChange(k, e.target.value)} data-testid={`payroll-${k}-input`} />
                      {hint && <p className="text-[10px] text-slate-500">{hint}</p>}
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {Object.entries(config.bvg_age_rates).map(([band, v]) => (
                    <div className="space-y-1" key={band}>
                      <Label className="text-xs">Alter {band}: Sparbeitrag total (%)</Label>
                      <Input type="number" step="0.1" value={v}
                        onChange={(e) => setConfig((prev) => ({ ...prev, bvg_age_rates: { ...prev.bvg_age_rates, [band]: e.target.value } }))}
                        data-testid={`payroll-bvg-age-${band}-input`} />
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Arbeitgeber-Anteil (%)</Label>
                    <Input type="number" value={config.bvg_employer_share} onChange={(e) => handleChange("bvg_employer_share", e.target.value)} data-testid="payroll-bvg-employer-share-input" />
                    <p className="text-[10px] text-slate-500">mind. 50 %, Rest trägt der Mitarbeiter</p>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Risikoprämie unter 25 (%, total)</Label>
                    <Input type="number" step="0.1" value={config.bvg_risk_rate_under_25} onChange={(e) => handleChange("bvg_risk_rate_under_25", e.target.value)} data-testid="payroll-bvg-risk-input" />
                    <p className="text-[10px] text-slate-500">0 = kein Abzug unter 25</p>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Satz ohne Geburtsdatum (%, total)</Label>
                    <Input type="number" step="0.1" value={config.bvg_default_rate_total} onChange={(e) => handleChange("bvg_default_rate_total", e.target.value)} data-testid="payroll-bvg-default-input" />
                  </div>
                </div>
                <p className="text-[11px] text-slate-600">Pro Mitarbeiter einstellbar: BVG-befreit, eigener Satz oder Fixbetrag (Mitarbeitende → Bearbeiten). Alter wird aus dem Geburtsdatum berechnet.</p>
              </>
            ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1">
              <Label htmlFor="bvg-emp">BVG AN (%)</Label>
              <Input
                id="bvg-emp"
                type="number"
                value={config.bvg_rate_employee}
                onChange={(e) => handleChange("bvg_rate_employee", e.target.value)}
                data-testid="payroll-bvg-employee-input"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="bvg-er">BVG AG (%)</Label>
              <Input
                id="bvg-er"
                type="number"
                value={config.bvg_rate_employer}
                onChange={(e) => handleChange("bvg_rate_employer", e.target.value)}
                data-testid="payroll-bvg-employer-input"
              />
            </div>
          </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1">
              <Label htmlFor="ins-emp">UVG/KTG AN (%)</Label>
              <Input
                id="ins-emp"
                type="number"
                value={config.insurance_rate_employee}
                onChange={(e) => handleChange("insurance_rate_employee", e.target.value)}
                data-testid="payroll-insurance-employee-input"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="ins-er">UVG/KTG AG (%)</Label>
              <Input
                id="ins-er"
                type="number"
                value={config.insurance_rate_employer}
                onChange={(e) => handleChange("insurance_rate_employer", e.target.value)}
                data-testid="payroll-insurance-employer-input"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1">
              <Label htmlFor="fak-canton">FAK-Kanton (Familienausgleichskasse)</Label>
              <select
                id="fak-canton"
                className="border border-slate-200 rounded-md px-2 py-1.5 text-sm w-full"
                value={config.fak_canton}
                onChange={(e) => {
                  const canton = e.target.value;
                  setConfig((prev) => ({
                    ...prev,
                    fak_canton: canton,
                    fak_rate_employer: fakRates[canton] != null ? String(fakRates[canton]) : prev.fak_rate_employer,
                  }));
                }}
                data-testid="payroll-fak-canton-select"
              >
                {(Object.keys(fakRates).length ? Object.keys(fakRates) : ["ZH"]).map((c) => (
                  <option key={c} value={c}>
                    {c}{fakRates[c] != null ? ` (${fakRates[c]}%)` : ""}
                  </option>
                ))}
              </select>
              <p className="text-[11px] text-slate-400">Offizieller Satz 2026 wird vorgeschlagen — bei abweichender Kasse überschreiben.</p>
            </div>
            <div className="space-y-1">
              <Label htmlFor="fak-er">FAK AG (%) — nur Arbeitgeber</Label>
              <Input
                id="fak-er"
                type="number"
                step="0.001"
                value={config.fak_rate_employer}
                onChange={(e) => handleChange("fak_rate_employer", e.target.value)}
                data-testid="payroll-fak-employer-input"
              />
            </div>
          </div>

          <div className="flex justify-end">
            <Button
              type="submit"
              size="sm"
              className="rounded-full bg-slate-900 text-slate-50 px-4"
              disabled={saving}
              data-testid="payroll-config-save-button"
            >
              {saving ? "Speichern ..." : "Konfiguration speichern"}
            </Button>
          </div>
        </form>

        {loading && (
          <p className="mt-2 text-xs text-slate-400" data-testid="payroll-config-loading">
            Lade Konfiguration ...
          </p>
        )}
      </Card>
    </div>
  );
}
